import { useEffect, useRef, useState } from "react";
import { ChevronDown, CornerDownRight, ListChecks, Phone, Plus } from "lucide-react";
import { TaskCreateDrawer } from "@/components/task/TaskCreateDrawer";
import { QuickCallDrawer } from "./QuickCallDrawer";

/**
 * 3A action bar — the ONLY user-facing create CTAs on the Client360 Активності
 * (Timeline) tab + Задачі tab. «+ Задача ▾» {Задача собі · Follow-up з клієнтом}
 * and «📞 Зафіксувати дзвінок». NO «+ Активність» / «Дія-Запит» (doctrine §0).
 * Open state can be driven externally (Home deep-links) via openSignal.
 */
export function TaskActionBar({
  holdingId,
  canCreateTask,
  canLogCall,
  overdueAmount,
  openSignal,
}: {
  holdingId: string;
  canCreateTask: boolean;
  canLogCall: boolean;
  overdueAmount?: number | null;
  /** External trigger from Home deep-links: "task_create" | "follow_up" | "call_log". */
  openSignal?: { target: "task_create" | "follow_up" | "call_log"; nonce: number } | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [taskMode, setTaskMode] = useState<"self" | "follow_up" | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  // Home deep-link driver.
  useEffect(() => {
    if (!openSignal) return;
    if (openSignal.target === "call_log" && canLogCall) setCallOpen(true);
    else if (openSignal.target === "follow_up" && canCreateTask) setTaskMode("follow_up");
    else if (openSignal.target === "task_create" && canCreateTask) setTaskMode("self");
  }, [openSignal, canCreateTask, canLogCall]);

  return (
    <>
      <div className="flex items-center gap-2">
        {canCreateTask && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <Plus className="h-4 w-4" aria-hidden /> Задача <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 z-20 mt-1 w-56 rounded-lg border bg-background p-1.5 shadow-lg">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Створити</div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setTaskMode("self"); }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-primary/10 hover:text-primary"
                >
                  <ListChecks className="h-4 w-4" aria-hidden /> Задача собі
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); setTaskMode("follow_up"); }}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-primary/10 hover:text-primary"
                >
                  <CornerDownRight className="h-4 w-4" aria-hidden /> Follow-up з клієнтом
                </button>
              </div>
            )}
          </div>
        )}
        {canLogCall && (
          <button
            type="button"
            onClick={() => setCallOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" aria-hidden /> Зафіксувати дзвінок
          </button>
        )}
      </div>

      {/* canonical TaskCreateDrawer (D-031) — holding-locked, subtype prefilled */}
      <TaskCreateDrawer
        open={taskMode !== null}
        onClose={() => setTaskMode(null)}
        entry={{
          holdingId,
          source: "картка клієнта (Client360)",
          defaultSubtype: taskMode === "follow_up" ? "follow_up" : "generic",
        }}
      />
      <QuickCallDrawer
        open={callOpen}
        onClose={() => setCallOpen(false)}
        holdingId={holdingId}
        overdueAmount={overdueAmount}
      />
    </>
  );
}
